import { useState, useEffect } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { useOrg } from '../../../contexts/OrgContext'
import { logAuditEvent } from '../../../shared/lib/auditLog'
import InviteClientUserModal from '../components/InviteClientUserModal'

function ClientUsersAdmin() {
  const { clients } = useAssessment()
  const { currentOrg } = useOrg()
  const [clientUsers, setClientUsers] = useState([])
  const [orgMembers, setOrgMembers] = useState([])
  const [memberClients, setMemberClients] = useState({})
  const [lastLogins, setLastLogins] = useState({})
  const [loading, setLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [error, setError] = useState('')
  const [typeFilter, setTypeFilter] = useState('all') // 'all' | 'client' | 'org'
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [editingUser, setEditingUser] = useState(null)
  const [editRole, setEditRole] = useState('')
  const [savingRole, setSavingRole] = useState(false)
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const fetchClientUsers = async () => {
    try {
      // Get client IDs for the current org to scope the query
      const orgClientIds = clients.map(c => c.id)

      let data = []
      if (orgClientIds.length > 0) {
        const { data: cuData, error } = await supabase
          .from('client_users')
          .select(`
            id,
            created_at,
            user_id,
            client_id,
            profiles:user_id (
              id,
              email,
              full_name,
              role
            ),
            clients:client_id (
              id,
              name
            )
          `)
          .in('client_id', orgClientIds)
          .order('created_at', { ascending: false })

        if (error) throw error
        data = cuData || []
      }

      setClientUsers(data)
    } catch (err) {
      console.error('Error fetching client users:', err)
      setError('Failed to load client users')
    }
  }

  const fetchOrgMembers = async () => {
    if (!currentOrg?.id) return
    try {
      const { data, error } = await supabase
        .from('org_members')
        .select(`
          id,
          created_at,
          user_id,
          org_id,
          role,
          profiles:user_id (
            id,
            email,
            full_name,
            role
          )
        `)
        .eq('org_id', currentOrg.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setOrgMembers(data || [])
    } catch (err) {
      console.error('Error fetching org members:', err)
    }
  }

  const fetchMemberClients = async () => {
    if (!currentOrg?.id) return
    try {
      const { data, error } = await supabase
        .from('org_member_clients')
        .select(`
          member_id,
          client_id,
          clients:client_id (
            id,
            name
          )
        `)

      if (error) throw error

      // Group by member_id
      const grouped = {}
      for (const row of (data || [])) {
        if (!grouped[row.member_id]) grouped[row.member_id] = []
        grouped[row.member_id].push(row.clients)
      }
      setMemberClients(grouped)
    } catch (err) {
      console.error('Error fetching member clients:', err)
    }
  }

  const fetchLastLogins = async () => {
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('user_id, created_at')
        .eq('action', 'user.login')
        .order('created_at', { ascending: false })

      if (error) throw error
      // Keep only the most recent login per user
      const map = {}
      for (const row of (data || [])) {
        if (!map[row.user_id]) map[row.user_id] = row.created_at
      }
      setLastLogins(map)
    } catch (err) {
      console.error('Error fetching last logins:', err)
    }
  }

  const fetchAll = async () => {
    setLoading(true)
    await Promise.all([fetchClientUsers(), fetchOrgMembers(), fetchMemberClients(), fetchLastLogins()])
    setLoading(false)
  }

  useEffect(() => {
    fetchAll()
  }, [currentOrg?.id])

  // Build unified rows — exclude global_admin users (managed at platform level, not org level)
  const orgMemberRows = orgMembers
    .filter(om => om.profiles?.role !== 'global_admin')
    .map(om => {
      const effectiveIsOrgAdmin = om.role === 'org_admin'
      return {
        key: `org-${om.id}`,
        userType: 'org',
        userId: om.user_id,
        memberId: om.id,
        email: om.profiles?.email,
        fullName: om.profiles?.full_name,
        role: effectiveIsOrgAdmin ? 'Org Admin' : 'Org Member',
        isOrgAdmin: effectiveIsOrgAdmin,
        isGlobalAdmin: false,
        assignedClients: effectiveIsOrgAdmin ? (clients || []).map(c => ({ id: c.id, name: c.name })) : (memberClients[om.id] || []),
        createdAt: om.created_at,
        raw: om
      }
    })

  const allRows = [
    ...clientUsers
      .filter(cu => cu.profiles?.email) // Skip entries with no profile (pending invite or stale record)
      .map(cu => ({
        key: `client-${cu.id}`,
        userType: 'client',
        userId: cu.user_id,
        memberId: cu.id,
        email: cu.profiles?.email,
        fullName: cu.profiles?.full_name,
        role: 'Client Portal User',
        isOrgAdmin: false,
        assignedClients: cu.clients ? [cu.clients] : [],
        createdAt: cu.created_at,
        raw: cu
      })),
    ...orgMemberRows
  ]

  // Apply type and search filters
  const filteredRows = allRows.filter(r => {
    if (typeFilter !== 'all' && r.userType !== typeFilter) return false
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      return (r.fullName || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.role || '').toLowerCase().includes(q)
    }
    return true
  })

  const handleDeleteClientUser = async (userId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('No active session')

      const response = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Failed to delete user')

      const deletedUser = clientUsers.find(cu => cu.user_id === userId)
      logAuditEvent({
        category: 'change',
        action: 'user.deleted',
        description: `Deleted client user "${deletedUser?.profiles?.email || userId}"`,
        clientId: deletedUser?.client_id,
        clientName: deletedUser?.clients?.name,
        resourceType: 'user',
        resourceId: userId,
        metadata: { email: deletedUser?.profiles?.email }
      })

      fetchAll()
      setDeleteConfirm(null)
    } catch (err) {
      console.error('Error deleting client user:', err)
      setError(err.message || 'Failed to delete client user')
    }
  }

  const handleDeleteOrgMember = async (memberId, userId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('No active session')

      const member = orgMembers.find(om => om.id === memberId)

      // Fully remove user from all tables + auth via the API endpoint
      const res = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to delete user')
      }

      logAuditEvent({
        category: 'change',
        action: 'user.removed',
        description: `Removed org member "${member?.profiles?.email || userId}" from "${currentOrg?.name}"`,
        resourceType: 'user',
        resourceId: userId,
        metadata: { email: member?.profiles?.email, orgRole: member?.role }
      })

      fetchAll()
      setDeleteConfirm(null)
    } catch (err) {
      console.error('Error removing org member:', err)
      setError(err.message || 'Failed to remove organization member')
    }
  }

  const handleDelete = (row) => {
    if (row.userType === 'client') {
      handleDeleteClientUser(row.userId)
    } else {
      handleDeleteOrgMember(row.memberId, row.userId)
    }
  }

  const handleToggleSelect = (key) => {
    setSelectedRows(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedRows.size === filteredRows.length) {
      setSelectedRows(new Set())
    } else {
      setSelectedRows(new Set(filteredRows.map(r => r.key)))
    }
  }

  const handleEditUser = (row) => {
    setEditingUser(row)
    if (row.userType === 'org') {
      setEditRole(row.raw.role) // org_admin or org_member
    } else {
      setEditRole('client') // client users only have one role
    }
  }

  const handleSaveRole = async () => {
    if (!editingUser || editingUser.userType !== 'org') return
    setSavingRole(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`/api/organizations/${currentOrg.id}/members/${editingUser.memberId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: editRole })
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to update role')
      }
      setEditingUser(null)
      fetchAll()
    } catch (err) {
      console.error('Error updating role:', err)
      setError(err.message)
    } finally {
      setSavingRole(false)
    }
  }

  const handleBulkDelete = async () => {
    setBulkDeleting(true)
    try {
      const rowsToDelete = filteredRows.filter(r => selectedRows.has(r.key))
      for (const row of rowsToDelete) {
        if (row.userType === 'client') {
          await handleDeleteClientUser(row.userId)
        } else {
          await handleDeleteOrgMember(row.memberId, row.userId)
        }
      }
      setSelectedRows(new Set())
      setShowBulkDeleteConfirm(false)
      fetchAll()
    } catch (err) {
      console.error('Error in bulk delete:', err)
    } finally {
      setBulkDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Users</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage client portal users and organization members
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedRows.size === 1 && (() => {
            const row = filteredRows.find(r => selectedRows.has(r.key))
            return row ? (
              <button
                onClick={() => handleEditUser(row)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Edit User
              </button>
            ) : null
          })()}
          {selectedRows.size > 0 && (
            <button
              onClick={() => setShowBulkDeleteConfirm(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Remove ({selectedRows.size})
            </button>
          )}
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn-primary"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Invite User
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users..."
            className="w-72 pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="all">All Users ({allRows.length})</option>
          <option value="client">Client Portal Users ({allRows.filter(r => r.userType === 'client').length})</option>
          <option value="org">Organization Members ({allRows.filter(r => r.userType === 'org').length})</option>
        </select>
      </div>

      {/* Error display */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
          <button
            onClick={() => setError('')}
            className="ml-2 underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {filteredRows.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No users yet</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Invite users to give them access to client portals or your organization
            </p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="btn-primary"
            >
              Invite Your First User
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={filteredRows.length > 0 && selectedRows.size === filteredRows.length}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Assigned Clients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Added
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredRows.map((row) => (
                  <tr key={row.key} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${selectedRows.has(row.key) ? 'bg-primary-50 dark:bg-primary-900/10' : ''}`}>
                    <td className="px-4 py-4 w-10">
                      {!row.isGlobalAdmin ? (
                        <input
                          type="checkbox"
                          checked={selectedRows.has(row.key)}
                          onChange={() => handleToggleSelect(row.key)}
                          className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700"
                        />
                      ) : <span />}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          row.userType === 'org'
                            ? 'bg-gradient-to-br from-purple-400 to-purple-600'
                            : 'bg-gradient-to-br from-primary-400 to-primary-600'
                        }`}>
                          <span className="text-white font-medium">
                            {row.fullName?.[0] || row.email?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {row.fullName || 'No name'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {row.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        row.isGlobalAdmin
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                          : row.userType === 'org'
                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                            : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      }`}>
                        {row.isGlobalAdmin ? 'Global Admin' : row.userType === 'org' ? 'Organization' : 'Client Portal'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 dark:text-white">
                        {row.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {row.isOrgAdmin ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                            All
                          </span>
                        ) : (
                          row.assignedClients.map(c => (
                            <span key={c.id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-accent-200">
                              {c.name}
                            </span>
                          ))
                        )}
                        {!row.isOrgAdmin && row.assignedClients.length === 0 && (
                          <span className="text-xs text-gray-400 italic">None</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(row.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {lastLogins[row.userId]
                        ? new Date(lastLogins[row.userId]).toLocaleDateString()
                        : <span className="text-gray-400 dark:text-gray-500 italic">Never</span>
                      }
                    </td>
                    <td className="px-4 py-4 w-10">
                      <button
                        onClick={() => handleEditUser(row)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        title="Edit user"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteClientUserModal
          clients={clients}
          currentOrg={currentOrg}
          onClose={() => setShowInviteModal(false)}
          onSuccess={fetchAll}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {deleteConfirm.userType === 'client' ? 'Delete Client User?' : 'Remove Organization Member?'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {deleteConfirm.userType === 'client' ? (
                  <>This will permanently delete <span className="font-medium">{deleteConfirm.email}</span> and remove all access to <span className="font-medium">{deleteConfirm.assignedClients?.[0]?.name}</span>. This action cannot be undone.</>
                ) : (
                  <>This will remove <span className="font-medium">{deleteConfirm.email}</span> from <span className="font-medium">{currentOrg?.name}</span>. They will lose admin portal access.</>
                )}
              </p>
            </div>
            <div className="flex justify-center space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                {deleteConfirm.userType === 'client' ? 'Delete User' : 'Remove Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          clients={clients}
          currentOrg={currentOrg}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); fetchAll() }}
        />
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Remove {selectedRows.size} User{selectedRows.size > 1 ? 's' : ''}?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                This will remove the selected users. Client portal users will be permanently deleted. Organization members will lose admin portal access.
              </p>
            </div>
            <div className="flex justify-center gap-3">
              <button onClick={() => setShowBulkDeleteConfirm(false)} className="btn-secondary">Cancel</button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {bulkDeleting ? 'Removing...' : 'Remove All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function EditUserModal({ user, clients, currentOrg, onClose, onSaved }) {
  const [fullName, setFullName] = useState(user.fullName || '')
  const [role, setRole] = useState(user.raw?.role || (user.userType === 'client' ? 'client' : 'org_member'))
  const [assignedClientIds, setAssignedClientIds] = useState(
    (user.assignedClients || []).map(c => c.id)
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const isGlobalAdmin = user.isGlobalAdmin

  const handleToggleClient = (clientId) => {
    setAssignedClientIds(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    )
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      // 1. Update profile name
      if (fullName.trim() !== (user.fullName || '')) {
        const { error: nameErr } = await supabase
          .from('profiles')
          .update({ full_name: fullName.trim() })
          .eq('id', user.userId)
        if (nameErr) throw new Error('Failed to update name')
      }

      const previousRole = user.raw?.role || (user.userType === 'client' ? 'client' : 'org_member')
      const previousType = user.userType // 'org' or 'client'
      const newIsOrg = ['org_member', 'org_admin'].includes(role)
      const newIsClient = role === 'client'
      const currentUserId = (await supabase.auth.getUser()).data.user?.id

      // 2. Update profile role
      if (role !== previousRole) {
        await supabase.from('profiles').update({ role }).eq('id', user.userId)
      }

      // 3. Handle account type transitions
      if (previousType === 'client' && newIsOrg) {
        // Client → Org: remove from client_users, add to org_members
        await supabase.from('client_users').delete().eq('user_id', user.userId)
        if (currentOrg?.id) {
          await supabase.from('org_members').upsert({
            user_id: user.userId,
            org_id: currentOrg.id,
            role: role
          }, { onConflict: 'user_id,org_id' })
        }
      } else if (previousType === 'org' && newIsClient) {
        // Org → Client: remove from org_members + org_member_clients, add to client_users
        if (user.memberId) {
          try { await supabase.from('org_member_clients').delete().eq('member_id', user.memberId) } catch {}
          await supabase.from('org_members').delete().eq('id', user.memberId)
        }
        if (assignedClientIds.length > 0) {
          await supabase.from('client_users').upsert({
            user_id: user.userId,
            client_id: assignedClientIds[0],
            invited_by: currentUserId
          }, { onConflict: 'user_id,client_id' })
        }
      } else if (previousType === 'org' && newIsOrg) {
        // Org → Org (role change): update org_members role
        if (user.memberId && role !== previousRole && currentOrg?.id) {
          const { data: { session } } = await supabase.auth.getSession()
          await fetch(`/api/organizations/${currentOrg.id}/members/${user.memberId}`, {
            method: 'PATCH',
            headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ role })
          })
        }
      }

      // 4. Update client assignments
      if (newIsClient && assignedClientIds.length > 0) {
        // Client portal user: ensure correct client assignment
        const currentClientId = user.assignedClients?.[0]?.id
        const newClientId = assignedClientIds[0]
        if (currentClientId && currentClientId !== newClientId) {
          await supabase.from('client_users').delete().eq('user_id', user.userId).eq('client_id', currentClientId)
        }
        if (currentClientId !== newClientId) {
          await supabase.from('client_users').upsert({
            user_id: user.userId,
            client_id: newClientId,
            invited_by: currentUserId
          }, { onConflict: 'user_id,client_id' })
        }
      } else if (newIsOrg && role === 'org_member') {
        // Org member: update client visibility assignments
        try {
          const { data: currentAssignments } = await supabase
            .from('org_member_clients')
            .select('client_id')
            .eq('member_id', user.memberId)

          const currentIds = (currentAssignments || []).map(a => a.client_id)
          const toAdd = assignedClientIds.filter(id => !currentIds.includes(id))
          const toRemove = currentIds.filter(id => !assignedClientIds.includes(id))

          for (const clientId of toAdd) {
            await supabase.from('org_member_clients').insert({ member_id: user.memberId, client_id: clientId })
          }
          for (const clientId of toRemove) {
            await supabase.from('org_member_clients').delete().eq('member_id', user.memberId).eq('client_id', clientId)
          }
        } catch {
          // org_member_clients table may not exist
        }
      }

      onSaved()
    } catch (err) {
      console.error('Error saving user:', err)
      setError(err.message || 'Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Edit User</h2>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{user.email}</p>
        </div>

        <div className="px-6 py-4 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Account Type & Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Type</label>
            {user.userType === 'client' ? (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg">
                  Client Portal User
                </p>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                  To change account type, remove this user and re-invite with the correct role.
                </p>
              </>
            ) : (
              <>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="org_member">Org Member</option>
                  <option value="org_admin">Org Admin</option>
                </select>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                  {role === 'org_member' && 'Access to admin portal with limited client visibility.'}
                  {role === 'org_admin' && 'Full access to admin portal and all clients.'}
                </p>
              </>
            )}
          </div>

          {/* Client Assignments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {role === 'client' ? 'Assigned Client' : 'Client Access'}
            </label>
            {role === 'org_admin' ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg">
                Org Admins have access to all clients
              </p>
            ) : role === 'client' ? (
              <select
                value={assignedClientIds[0] || ''}
                onChange={(e) => setAssignedClientIds(e.target.value ? [e.target.value] : [])}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select client...</option>
                {(clients || []).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2">
                {(clients || []).length === 0 ? (
                  <p className="text-xs text-gray-400 dark:text-gray-500 p-2">No clients available</p>
                ) : (
                  (clients || []).map(c => (
                    <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={assignedClientIds.includes(c.id)}
                        onChange={() => handleToggleClient(c.id)}
                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{c.name}</span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ClientUsersAdmin
